import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 法律常识 - 竖屏「法条解读」风格
 *
 * 核心亮点：
 * - 法条文本逐行展开 + 关键词高亮
 * - 天平动画（公正象征）
 * - 案例卡片弹入
 * - 条文编号强调
 * - 庄重的深色 + 金色配色
 *
 * 尺寸：1080x1920 竖屏
 */

interface LawArticle {
  number: string;
  text: string;
  highlights: string[];
}

interface LawCase {
  title: string;
  result: string;
  icon: string;
}

interface LawExplainerTemplateProps {
  topic: string;
  subtitle: string;
  lawName: string;
  articles: LawArticle[];
  cases: LawCase[];
  keyPoints: string[];
  tip: string;
}

const DEFAULT_PROPS: LawExplainerTemplateProps = {
  topic: '劳动法：加班工资怎么算？',
  subtitle: '《劳动法》第四十四条解读',
  lawName: '中华人民共和国劳动法',
  articles: [
    {
      number: '第四十四条',
      text: '有下列情形之一的，用人单位应当按照下列标准支付高于劳动者正常工作时间工资的工资报酬',
      highlights: ['高于', '工资报酬'],
    },
    {
      number: '（一）',
      text: '安排劳动者延长工作时间的，支付不低于工资的 150% 的工资报酬',
      highlights: ['150%'],
    },
    {
      number: '（二）',
      text: '休息日安排劳动者工作又不能安排补休的，支付不低于工资的 200% 的工资报酬',
      highlights: ['200%', '休息日'],
    },
    {
      number: '（三）',
      text: '法定休假日安排劳动者工作的，支付不低于工资的 300% 的工资报酬',
      highlights: ['300%', '法定休假日'],
    },
  ],
  cases: [
    { title: '周末加班未付双倍工资', result: '法院判决公司补发差额', icon: '⚖️' },
    { title: '春节加班仅付正常工资', result: '仲裁裁决支付 3 倍工资', icon: '🔨' },
  ],
  keyPoints: [
    '工作日加班 → 1.5 倍工资',
    '周末加班 → 2 倍工资（或补休）',
    '法定节假日加班 → 3 倍工资（不能补休）',
    '加班需保留证据（打卡记录、聊天记录等）',
  ],
  tip: '遇到加班不付工资的情况，可向当地劳动监察大队投诉或申请劳动仲裁',
};

export const LawExplainerTemplate: React.FC<Partial<LawExplainerTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  // Balance scale animation
  const scaleSwing = interpolate(Math.sin(frame * 0.04), [-1, 1], [-6, 6]);
  const scaleVisible = interpolate(frame, [10, 35], [0, 1], { extrapolateRight: 'clamp' });

  // Articles
  const artStart = 60;
  const artInterval = 55;

  // Cases
  const caseStart = artStart + p.articles.length * artInterval + 20;
  const caseInterval = 45;

  // Key points
  const kpStart = caseStart + p.cases.length * caseInterval + 20;

  // Tip
  const tipStart = kpStart + p.keyPoints.length * 25 + 30;
  const tipOpacity = interpolate(frame, [tipStart, tipStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  const highlightText = (text: string, highlights: string[]) => {
    let result: React.ReactNode[] = [text];
    for (const hl of highlights) {
      const newResult: React.ReactNode[] = [];
      for (const part of result) {
        if (typeof part !== 'string') {
          newResult.push(part);
          continue;
        }
        const segments = part.split(new RegExp(`(${hl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'g'));
        segments.forEach((seg, i) => {
          if (seg === hl) {
            newResult.push(
              <span key={`${hl}-${i}`} style={{ color: '#fbbf24', fontWeight: 800, borderBottom: '2px solid #fbbf24' }}>{seg}</span>
            );
          } else if (seg) {
            newResult.push(seg);
          }
        });
      }
      result = newResult;
    }
    return result;
  };

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Serif SC", serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: headerProgress,
        }}
      >
        {/* Balance scale SVG */}
        <svg width="80" height="60" viewBox="0 0 80 60" style={{ margin: '0 auto 12px', display: 'block', opacity: scaleVisible }}>
          <line x1="40" y1="8" x2="40" y2="55" stroke="#fbbf24" strokeWidth="3" />
          <line x1="10" y1="25" x2="70" y2="25" stroke="#fbbf24" strokeWidth="2.5" strokeLinecap="round" transform={`rotate(${scaleSwing}, 40, 25)`} />
          <circle cx="40" cy="8" r="5" fill="#fbbf24" />
          <g transform={`rotate(${scaleSwing}, 40, 25)`}>
            <path d="M8,25 L4,40 L16,40 Z" fill="#fbbf2440" stroke="#fbbf24" strokeWidth="1" />
            <path d="M72,25 L64,40 L76,40 Z" fill="#fbbf2440" stroke="#fbbf24" strokeWidth="1" />
          </g>
          <rect x="30" y="52" width="20" height="6" rx="3" fill="#fbbf24" opacity="0.4" />
        </svg>

        <div style={{ padding: '4px 16px', display: 'inline-block', background: '#fbbf2415', border: '1px solid #fbbf2430', borderRadius: 8, fontSize: 30, color: '#fbbf24', fontWeight: 600, marginBottom: 12 }}>
          ⚖️ {p.lawName}
        </div>
        <h1 style={{ fontSize: 48, fontWeight: 800, margin: '0 60px', lineHeight: 1.3 }}>{p.topic}</h1>
        <p style={{ fontSize: 40, color: 'rgba(255,255,255,0.3)', marginTop: 6 }}>{p.subtitle}</p>
      </div>

      {/* ========== LAW ARTICLES ========== */}
      <div
        style={{
          position: 'absolute',
          top: 400,
          left: 60,
          right: 60,
        }}
      >
        {p.articles.map((art, i) => {
          const start = artStart + i * artInterval;
          const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
          const x = interpolate(frame, [start, start + 20], [15, 0], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                marginBottom: 18,
                opacity,
                transform: `translateX(${x}px)`,
                display: 'flex',
                gap: 14,
              }}
            >
              <div
                style={{
                  padding: '4px 12px',
                  background: '#fbbf2415',
                  border: '1px solid #fbbf2430',
                  borderRadius: 8,
                  fontSize: 36,
                  fontWeight: 700,
                  color: '#fbbf24',
                  whiteSpace: 'nowrap',
                  height: 'fit-content',
                }}
              >
                {art.number}
              </div>
              <div style={{ fontSize: 34, lineHeight: 1.6, color: 'rgba(255,255,255,0.8)' }}>
                {highlightText(art.text, art.highlights)}
              </div>
            </div>
          );
        })}
      </div>

      {/* ========== CASES ========== */}
      <div
        style={{
          position: 'absolute',
          top: 900,
          left: 60,
          right: 60,
        }}
      >
        <div style={{ fontSize: 40, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em', marginBottom: 14, marginTop: 50 }}>📋 典型案例</div>
        {p.cases.map((c, i) => {
          const start = caseStart + i * caseInterval;
          const progress = spring({
            frame: frame - start,
            fps,
            from: 0,
            to: 1,
            config: { damping: 12, stiffness: 130, mass: 0.5 },
          });

          return (
            <div
              key={i}
              style={{
                padding: '18px 22px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: 16,
                border: '1px solid rgba(255,255,255,0.06)',
                marginBottom: 12,
                opacity: progress,
                transform: `translateY(${(1 - progress) * 12}px)`,
                display: 'flex',
                alignItems: 'center',
                gap: 16,
              }}
            >
              <span style={{ fontSize: 36 }}>{c.icon}</span>
              <div>
                <div style={{ fontSize: 38, fontWeight: 700, marginBottom: 4 }}>{c.title}</div>
                <div style={{ fontSize: 36, color: '#22c55e' }}>→ {c.result}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ========== KEY POINTS ========== */}
      <div
        style={{
          position: 'absolute',
          top: 1300,
          left: 60,
          right: 60,
        }}
      >
        <div style={{ fontSize: 40, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em', marginBottom: 14, marginTop: 80 }}>🎯 核心要点</div>
        {p.keyPoints.map((kp, i) => {
          const start = kpStart + i * 25;
          const opacity = interpolate(frame, [start, start + 18], [0, 1], { extrapolateRight: 'clamp' });
          return (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 14, opacity }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fbbf24', marginTop: 18, flexShrink: 0 }} />
              <span style={{ fontSize: 36, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>{kp}</span>
            </div>
          );
        })}
      </div>

      {/* Tip */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          left: 60,
          right: 60,
          opacity: tipOpacity,
        }}
      >
        <div
          style={{
            padding: '18px 24px',
            background: 'rgba(251,191,36,0.06)',
            border: '1px solid rgba(251,191,36,0.2)',
            borderRadius: 18,
          }}
        >
          <div style={{ fontSize: 15, color: '#fbbf24', fontWeight: 600, marginBottom: 6 }}>💡 实用建议</div>
          <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>{p.tip}</div>
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #fbbf24, #ef4444)',
        }}
      />
    </AbsoluteFill>
  );
};
